/**
 * popup-core.js
 *
 * Shared trigger + frequency engine for every popup section in this theme
 * (sections/email-popup.liquid, sections/promo-popup.liquid). Each popup is a
 * `[data-ep]` element whose data-* attributes carry its section settings, so
 * all behaviour is merchant-controlled from the theme editor.
 *
 * `data-storage-key` namespaces the frequency state, so two popups on the same
 * store never suppress each other. Instances register on window.themePopups
 * keyed by that value -- e.g. `window.themePopups['promo-popup'].open()`.
 * `window.emailPopup` is kept as an alias for the email popup.
 *
 * Popups with a form (the email popup) submit through Shopify's native customer
 * form tag, which is a full page POST followed by a redirect back to the same
 * page with ?customer_posted=true and #<form_id> appended. Two consequences:
 *
 *   1. form.posted_successfully? is true for EVERY customer form on the page
 *      once any one of them posts (the footer newsletter, for instance). The
 *      fragment is the only reliable discriminator, so the success state is
 *      only honoured when location.hash matches that popup's form id.
 *   2. The success markup is rendered server-side, so on that load the trigger
 *      is skipped and the popup opens immediately.
 *
 * Form-free popups simply have no [data-ep-success] element and no form id,
 * so both checks are inert.
 *
 * Signup memory: the email is stashed as "pending" when the form submits and
 * promoted to "signed-up" once the success page loads, so a failed post never
 * counts. A later submit of that same email is blocked in the browser and the
 * popup swaps to its [data-ep-already] message instead of revealing the code.
 */
(function () {
  'use strict';

  var DAY_MS = 86400000;

  // localStorage throws in Safari private mode and when cookies are blocked.
  // Failing to read a suppression flag should never stop the popup working.
  function readStore(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function writeStore(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* non-fatal */ }
  }

  function daysSince(raw) {
    var then = parseInt(raw, 10);
    if (!then) return Infinity;
    return (Date.now() - then) / DAY_MS;
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function setup(root) {
    if (root.__epReady) return;
    root.__epReady = true;

    // Each popup namespaces its own frequency state, so an email popup and a
    // promo popup on the same store never suppress one another.
    var ns = root.getAttribute('data-storage-key') || 'popup';
    var STORE_KEY = ns + ':last-shown';
    var SIGNUP_KEY = ns + ':signed-up';
    var EMAIL_KEY = ns + ':signed-up-email';
    var PENDING_KEY = ns + ':pending-email';
    var SESSION_KEY = ns + ':session';

    var dialog = root.querySelector('[data-ep-dialog]');
    var success = root.querySelector('[data-ep-success]');
    var form = root.querySelector('form');
    var signupView = root.querySelector('[data-ep-signup]');
    var alreadyView = root.querySelector('[data-ep-already]');
    var formId = root.getAttribute('data-form-id');
    var opts = {
      trigger: root.getAttribute('data-trigger') || 'delay',
      delay: parseFloat(root.getAttribute('data-delay')) || 0,
      scrollPercent: parseFloat(root.getAttribute('data-scroll-percent')) || 40,
      exitMobileDelay: parseFloat(root.getAttribute('data-exit-mobile-delay')) || 0,
      frequency: root.getAttribute('data-frequency') || 'days',
      frequencyDays: parseFloat(root.getAttribute('data-frequency-days')) || 14,
      suppressAfterSignup: root.getAttribute('data-suppress-after-signup') === 'true',
      closeOnBackdrop: root.getAttribute('data-close-on-backdrop') === 'true',
    };

    // This popup's own form posted (rather than some other customer form).
    var postedHere = !!success && window.location.hash === '#' + formId;
    var opened = false;
    var lastFocus = null;

    function open(force) {
      if (opened) return;
      if (!force && !isDesignMode()) markShown();
      opened = true;
      lastFocus = document.activeElement;
      root.removeAttribute('hidden');
      // Force a reflow so the transition runs instead of snapping open.
      void root.offsetWidth;
      root.classList.add('is-open');
      document.documentElement.classList.add('ep-open');

      var focusTarget =
        root.querySelector('[data-ep-copy]') ||
        root.querySelector('input[type="email"]') ||
        root.querySelector('[data-ep-close]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    function close() {
      if (!opened) return;
      opened = false;
      root.classList.remove('is-open');
      document.documentElement.classList.remove('ep-open');
      window.setTimeout(function () {
        if (!opened) root.setAttribute('hidden', '');
      }, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    function markShown() {
      writeStore('localStorage', STORE_KEY, String(Date.now()));
      writeStore('sessionStorage', SESSION_KEY, '1');
    }

    function normaliseEmail(value) {
      return String(value || '').trim().toLowerCase();
    }

    function showAlreadySignedUp() {
      if (!alreadyView) return;
      if (signupView) signupView.setAttribute('hidden', '');
      alreadyView.removeAttribute('hidden');
      var btn = alreadyView.querySelector('[data-ep-close]');
      if (btn) btn.focus({ preventScroll: true });
    }

    // Should the popup be shown at all on this page load?
    function suppressed() {
      if (isDesignMode()) return false; // always previewable while editing

      if (opts.suppressAfterSignup && readStore('localStorage', SIGNUP_KEY)) return true;

      if (opts.frequency === 'always') return false;
      if (opts.frequency === 'session') {
        return readStore('sessionStorage', SESSION_KEY) === '1';
      }
      // 'days'
      return daysSince(readStore('localStorage', STORE_KEY)) < opts.frequencyDays;
    }

    // --- Triggers -----------------------------------------------------------

    function armDelay(seconds) {
      window.setTimeout(function () { open(); }, Math.max(0, seconds) * 1000);
    }

    function armScroll() {
      var fired = false;
      function onScroll() {
        if (fired) return;
        var doc = document.documentElement;
        var scrollable = doc.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return;
        var pct = (window.pageYOffset / scrollable) * 100;
        if (pct < opts.scrollPercent) return;
        fired = true;
        window.removeEventListener('scroll', onScroll);
        open();
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    function armExit() {
      // Exit intent needs a real pointer. Touch devices fall back to a delay,
      // or opt out entirely when that delay is set to 0.
      var hasMouse = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!hasMouse) {
        if (opts.exitMobileDelay > 0) armDelay(opts.exitMobileDelay);
        return;
      }
      function onLeave(e) {
        if (e.clientY > 0) return; // only toward the browser chrome
        document.removeEventListener('mouseout', onLeave);
        open();
      }
      document.addEventListener('mouseout', onLeave);
    }

    function arm() {
      switch (opts.trigger) {
        case 'immediate': open(); break;
        case 'scroll': armScroll(); break;
        case 'exit': armExit(); break;
        default: armDelay(opts.delay);
      }
    }

    // --- Wiring -------------------------------------------------------------

    Array.prototype.forEach.call(root.querySelectorAll('[data-ep-close]'), function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        if (!isDesignMode()) markShown(); // an explicit dismissal counts as shown
        close();
      });
    });

    if (opts.closeOnBackdrop) {
      root.addEventListener('click', function (e) {
        if (dialog && !dialog.contains(e.target)) close();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && opened) close();
    });

    // Keep tabbing inside the dialog while it is open.
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !dialog) return;
      var focusable = dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    if (form) {
      form.addEventListener('submit', function (e) {
        var input = form.querySelector('input[type="email"]');
        var email = normaliseEmail(input && input.value);
        if (!email) return;
        if (email === normaliseEmail(readStore('localStorage', EMAIL_KEY))) {
          e.preventDefault();
          showAlreadySignedUp();
          return;
        }
        writeStore('localStorage', PENDING_KEY, email);
      });
    }

    var copyBtn = root.querySelector('[data-ep-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var codeEl = root.querySelector('[data-ep-code]');
        if (!codeEl) return;
        var code = codeEl.textContent.trim();
        var done = function () {
          copyBtn.textContent = copyBtn.getAttribute('data-copied-label') || 'Copied!';
          window.setTimeout(function () {
            copyBtn.textContent = copyBtn.getAttribute('data-label') || 'Copy';
          }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done).catch(function () {});
        } else {
          // Older Safari / non-secure contexts have no async clipboard API.
          var tmp = document.createElement('textarea');
          tmp.value = code;
          tmp.setAttribute('readonly', '');
          tmp.style.position = 'fixed';
          tmp.style.opacity = '0';
          document.body.appendChild(tmp);
          tmp.select();
          try { document.execCommand('copy'); done(); } catch (e) { /* leave it selectable */ }
          document.body.removeChild(tmp);
        }
      });
    }

    // --- Boot ---------------------------------------------------------------

    if (postedHere) {
      // Signup just happened: show the confirmation regardless of frequency,
      // and remember it so we stop pestering them.
      writeStore('localStorage', SIGNUP_KEY, String(Date.now()));
      var pending = readStore('localStorage', PENDING_KEY);
      if (pending) {
        writeStore('localStorage', EMAIL_KEY, pending);
        try { localStorage.removeItem(PENDING_KEY); } catch (e) { /* non-fatal */ }
      }
      open(true);
    } else if (isDesignMode()) {
      // In the theme editor the popup stays out of the way until you actually
      // work on it -- otherwise it covers the page while you edit every other
      // section. The section:load / section:select handlers below open it when
      // this section is selected or its settings change.
    } else if (!suppressed()) {
      arm();
    }

    // Handle for the editor listeners, which address the instance by element.
    root.__epApi = { open: function () { open(true); }, close: close };

    window.themePopups = window.themePopups || {};
    window.themePopups[ns] = {
      open: function () { open(true); },
      close: close,
      reset: function () {
        try {
          localStorage.removeItem(STORE_KEY);
          localStorage.removeItem(SIGNUP_KEY);
          localStorage.removeItem(EMAIL_KEY);
          localStorage.removeItem(PENDING_KEY);
          sessionStorage.removeItem(SESSION_KEY);
        } catch (e) { /* nothing to clear */ }
        return 'cleared - reload to see the popup again';
      },
      state: function () {
        return {
          options: opts,
          postedHere: postedHere,
          suppressed: suppressed(),
          lastShown: readStore('localStorage', STORE_KEY),
          signedUp: readStore('localStorage', SIGNUP_KEY),
          signedUpEmail: readStore('localStorage', EMAIL_KEY),
        };
      },
    };

    // Back-compat handle for the email popup's documented console helper.
    if (ns === 'email-popup') window.emailPopup = window.themePopups[ns];
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ep]'), setup);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Shopify scopes these events to one section, so `event.target` is that
  // section's wrapper -- an event for the header or footer simply won't
  // contain our element and is ignored.
  function popupIn(event) {
    var target = event && event.target;
    if (!target || !target.querySelector) return null;
    return target.matches && target.matches('[data-ep]') ? target : target.querySelector('[data-ep]');
  }

  // Re-rendered after a setting changed: re-bind, then show it, since changing
  // one of its settings means the merchant is looking at this section.
  document.addEventListener('shopify:section:load', function (e) {
    init();
    var el = popupIn(e);
    if (el && el.__epApi) el.__epApi.open();
  });

  document.addEventListener('shopify:section:select', function (e) {
    var el = popupIn(e);
    if (el && el.__epApi) el.__epApi.open();
  });

  document.addEventListener('shopify:section:deselect', function (e) {
    var el = popupIn(e);
    if (el && el.__epApi) el.__epApi.close();
  });
})();
